import { useEffect, useState } from 'react'
import { useRecordContext, useNotify } from 'react-admin'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  Typography,
  CircularProgress,
} from '@mui/material'
import { sendLog } from '../../../../Class/AuditLog'
import { usePriceModalContext } from '../../../../Utils/providers/PriceModalProvider'
import {
  calculatePrices,
  getVehicleCondition,
} from '../../../../Utils/RestUtils'

const ConfirmationModal = () => {
  const record = useRecordContext()
  const notify = useNotify()
  const { activeModal, openModal, closeModal } = usePriceModalContext()
  const [isLoading, setIsLoading] = useState(true)
  const [vehicleConditionData, setVehicleConditionData] = useState(null)

  const handleEditCondition = async () => {
    try {
      await sendLog(
        record.originId,
        'Agent cancelled get offers to edit vehicle condition'
      )
      closeModal()
    } catch (error) {
      console.error(error)
      notify(error.message, { type: 'error' })
    }
  }

  const handleSubmit = async () => {
    try {
      await sendLog(record.originId, 'Agent requested price calculation')
      handleGetPriceData()
    } catch (error) {
      console.error(error)
      notify(error.message, { type: 'error' })
    }
  }

  const handleGetPriceData = async () => {
    if (isLoading) return
    setIsLoading(true)

    try {
      const response = await calculatePrices({ quoteId: record['@id'] })
      if (response.result !== 'success')
        throw new Error(response.error_msg || 'Failed to calculate prices')

      const offerJourneyFollowed =
        record.leadContact.vehicle.vehicleSalvage?.offerJourneyFollowed ?? false

      const ml_guide = Number(response.data.prediction.guide_price)
      const ml_max = Number(response.data.prediction.max_price)

      // Salvage exclusion always converts to scrap, regardless of the 70% rule
      if (response.is_salvage_exclusion && response.scrap) {
        openModal('scrap', { agent: response.scrap })
        return
      }

      let maxPrice = ml_guide

      if (response.scrap) {
        const sortPrice = Number(response.scrap.sort_price)

        if (sortPrice * 0.7 > ml_max) {
          openModal('scrap', { agent: response.scrap })
          return
        } else if (sortPrice > ml_max) {
          maxPrice = ml_max
        }
      }

      const displayMaxPrice = maxPrice.toFixed(2)

      openModal(offerJourneyFollowed ? 'bestPrice' : 'salvage', {
        maxPrice: displayMaxPrice,
        topScrapPrice: response.scrap?.sort_price ?? null,
      })
    } catch (error) {
      notify(error.message, { type: 'error' })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (activeModal !== 'confirmation') return
    if (!record.originId) return

    const fetchVehicleCondition = async () => {
      setIsLoading(true)
      try {
        const response = await getVehicleCondition(record.originId)
        setVehicleConditionData(response.result)
      } catch (error) {
        notify(error.message, { type: 'error' })
      } finally {
        setIsLoading(false)
      }
    }

    fetchVehicleCondition()
  }, [activeModal, record.originId, notify])

  // TODO replace spinner
  if (activeModal !== null && isLoading)
    return (
      <>
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      </>
    )

  return (
    <>
      <Dialog
        disablebackdropclick="true"
        disableEnforceFocus
        hideBackdrop
        disableScrollLock
        fullWidth
        open={activeModal === 'confirmation'}
        onClose={() => closeModal(false)}
        aria-label="Confimation modal"
        sx={{
          left: '50%',
          top: '50%',
          right: 'initial',
          bottom: 'initial',
          transform: 'translate(-50%, -50%)',
          maxWidth: '768px',
          width: '100%',
        }}
        PaperProps={{
          sx: {
            padding: 2,
            maxHeight: '80vh',
            overflowY: 'auto',
          },
        }}
      >
        <DialogContent sx={{ padding: 0 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="h6" fontWeight={'bold'}>
                  Confirm Vehicles Condition
                </Typography>
                <Typography>
                  Confirm the selected vehicle condition with the customer.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={0}>
                {vehicleConditionData &&
                  vehicleConditionData.map((condition, index) => (
                    <Grid item xs={4} key={index} sx={{ mb: 1 }}>
                      <Typography variant="body1">
                        <strong>{condition.name}</strong>
                        <br />
                        {condition.value}
                      </Typography>
                    </Grid>
                  ))}
              </Grid>
            </Grid>
          </Grid>
          <Divider sx={{ my: 2 }} />
          <DialogActions sx={{ padding: 0 }}>
            <Button label="ra.action.cancel" onClick={handleEditCondition}>
              Edit Condition
            </Button>
            <Button
              type="button"
              variant="contained"
              label="Accept offer"
              onClick={handleSubmit}
            >
              Confirm
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default ConfirmationModal
